import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Compass, ShieldCheck, Timer } from "lucide-react";
import { IMAGES } from "@/lib/content";

const Counter = ({ value, suffix, label, testid }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const duration = 1400;
        const start = performance.now();
        let frame;
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setCount(Math.round(eased * value));
            if (p < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [inView, value]);

    return (
        <div ref={ref} data-testid={testid} className="rounded-2xl border border-white/60 bg-white/70 backdrop-blur-xl px-4 py-5 shadow-lg shadow-slate-200/40">
            <div className="font-display font-extrabold text-2xl sm:text-3xl gradient-text whitespace-nowrap tracking-tight">
                {count}
                {suffix}
            </div>
            <div className="text-xs sm:text-sm text-slate-500 mt-1.5 leading-snug">{label}</div>
        </div>
    );
};

const ETHOS = ["Design-led engineering", "Founder-level craft", "Global mindset", "AI-inspired aesthetics", "Pixel perfection"];

export const About = () => {
    return (
        <section id="about" className="relative py-20 lg:py-32">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-6"
                >
                    <div className="font-mono-brand text-xs tracking-[0.25em] uppercase font-semibold text-violet-600 mb-4" data-testid="about-eyebrow">
                        About Brain Brush
                    </div>
                    <h2 className="font-display font-extrabold tracking-tight text-slate-900 text-2xl sm:text-3xl lg:text-4xl leading-snug">
                        Seven years of turning ambitious ideas into <span className="gradient-text">products people love.</span>
                    </h2>
                    <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
                        Brain Brush began with a simple belief: great digital products are painted with both precision
                        engineering and bold creative strokes. Today, our work serves over 5 lakh users across regional
                        and international markets, partnering with founders and teams who refuse to ship the ordinary.
                    </p>
                    <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
                        From the first sketch to the final deploy, every interface, interaction and line of code is
                        crafted in-house — design and development under one roof, with one obsessive standard.
                    </p>
                    <div className="mt-7 flex flex-wrap gap-2.5">
                        {ETHOS.map((tag) => (
                            <span
                                key={tag}
                                data-testid={`about-ethos-${tag.replace(/\s+/g, "-").toLowerCase()}`}
                                className="text-xs font-medium text-violet-700 bg-violet-50 border border-violet-100 rounded-full px-3.5 py-1.5"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, x: 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="lg:col-span-6"
                >
                    <div className="relative">
                        <div className="absolute -inset-5 gradient-bg opacity-15 blur-3xl rounded-full" />
                        <div className="relative rounded-[1.75rem] overflow-hidden border border-white/60 shadow-2xl shadow-violet-500/15">
                            <img
                                src={IMAGES.about}
                                alt="Fluid gradient sculpture symbolising Brain Brush's design philosophy"
                                className="w-full h-64 object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-6">
                        <Counter value={5} suffix="+" label="Lakh users" testid="about-stat-users" />
                        <Counter value={99} suffix="%" label="Client satisfaction" testid="about-stat-satisfaction" />
                        <Counter value={100} suffix="%" label="On-time delivery" testid="about-stat-delivery" />
                    </div>
                    <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-500">
                        <span className="inline-flex items-center gap-2"><Compass size={15} className="text-violet-600" /> Regional & international</span>
                        <span className="inline-flex items-center gap-2"><ShieldCheck size={15} className="text-pink-500" /> Enterprise-grade quality</span>
                        <span className="inline-flex items-center gap-2"><Timer size={15} className="text-amber-500" /> Rapid delivery cycles</span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
